import { forbidExtraProps, mutuallyExclusiveProps, nonNegativeInteger } from 'airbnb-prop-types';
import { addEventListener } from 'consolidated-events';
import { DateTime } from 'luxon'; // Assuming we keep Luxon DateTime
import PropTypes from 'prop-types';
import React from 'react';

import { CalendarDayPhrases } from '../defaultPhrases';
import getPhrasePropTypes from '../utils/getPhrasePropTypes';
import getTransformStyles from '../utils/getTransformStyles';
import isNextMonth from '../utils/isNextMonth';
import isPrevMonth from '../utils/isPrevMonth';
import toISOMonthString from '../utils/toISOMonthString';
import CalendarMonth from './CalendarMonth';

import getCalendarMonthWidth from '../utils/getCalendarMonthWidth';
import isTransitionEndSupported from '../utils/isTransitionEndSupported';

import DayOfWeekShape from '../shapes/DayOfWeekShape';
import ModifiersShape from '../shapes/ModifiersShape';
import ScrollableOrientationShape from '../shapes/ScrollableOrientationShape';

import { DAY_SIZE, HORIZONTAL_ORIENTATION, VERTICAL_ORIENTATION, VERTICAL_SCROLLABLE } from '../constants';
import cloneDateTime from '../utils/cloneDateTime';
import mergeClassNames from '../utils/mergeClassNames';
import mergeStyles from '../utils/mergeStyles';

const propTypes = forbidExtraProps({
    enableOutsideDays: PropTypes.bool,
    firstVisibleMonthIndex: PropTypes.number,
    horizontalMonthPadding: nonNegativeInteger,
    initialMonth: PropTypes.instanceOf(DateTime), // Keep using Luxon DateTime
    isAnimating: PropTypes.bool,
    numberOfMonths: PropTypes.number,
    modifiers: PropTypes.objectOf(PropTypes.objectOf(ModifiersShape)),
    orientation: ScrollableOrientationShape,
    onDayClick: PropTypes.func,
    onDayMouseEnter: PropTypes.func,
    onDayMouseLeave: PropTypes.func,
    onMonthTransitionEnd: PropTypes.func,
    onMonthChange: PropTypes.func,
    onYearChange: PropTypes.func,
    renderMonthText: mutuallyExclusiveProps(PropTypes.func, 'renderMonthText', 'renderMonthElement'),
    renderCalendarDay: PropTypes.func,
    renderDayContents: PropTypes.func,
    translationValue: PropTypes.number,
    renderMonthElement: mutuallyExclusiveProps(PropTypes.func, 'renderMonthText', 'renderMonthElement'),
    daySize: nonNegativeInteger,
    focusedDate: PropTypes.instanceOf(DateTime), // Keep using Luxon DateTime
    isFocused: PropTypes.bool,
    firstDayOfWeek: DayOfWeekShape,
    setMonthTitleHeight: PropTypes.func,
    isRTL: PropTypes.bool,
    transitionDuration: nonNegativeInteger,
    verticalBorderSpacing: nonNegativeInteger,

    // i18n
    monthFormat: PropTypes.string,
    phrases: PropTypes.shape(getPhrasePropTypes(CalendarDayPhrases)),
    dayAriaLabelFormat: PropTypes.string,
});

const defaultProps = {
    enableOutsideDays: false,
    firstVisibleMonthIndex: 0,
    horizontalMonthPadding: 13,
    initialMonth: DateTime.now(), // Default to Luxon DateTime
    isAnimating: false,
    numberOfMonths: 1,
    modifiers: {},
    orientation: HORIZONTAL_ORIENTATION,
    onDayClick() {},
    onDayMouseEnter() {},
    onDayMouseLeave() {},
    onMonthChange() {},
    onYearChange() {},
    onMonthTransitionEnd() {},
    renderMonthText: null,
    renderCalendarDay: undefined,
    renderDayContents: null,
    translationValue: null,
    renderMonthElement: null,
    daySize: DAY_SIZE,
    focusedDate: null,
    isFocused: false,
    firstDayOfWeek: null,
    setMonthTitleHeight: null,
    isRTL: false,
    transitionDuration: 0,
    verticalBorderSpacing: undefined,

    // i18n
    monthFormat: 'MMMM yyyy',
    phrases: CalendarDayPhrases,
    dayAriaLabelFormat: undefined,
};

function getMonths(initialMonth, numberOfMonths, withoutTransitionMonths) {
    let month = cloneDateTime(initialMonth);
    if (!withoutTransitionMonths) month = month.minus({ months: 1 });

    const months = [];
    for (let i = 0; i < (withoutTransitionMonths ? numberOfMonths : numberOfMonths + 2); i += 1) {
        months.push(month);
        month = cloneDateTime(month).plus({ months: 1 });
    }

    return months;
}

class CalendarMonthGrid extends React.PureComponent {
    constructor(props) {
        super(props);
        const withoutTransitionMonths = props.orientation === VERTICAL_SCROLLABLE;
        this.state = {
            months: getMonths(props.initialMonth, props.numberOfMonths, withoutTransitionMonths),
        };

        this.isTransitionEndSupported = isTransitionEndSupported();
        this.onTransitionEnd = this.onTransitionEnd.bind(this);
        this.setContainerRef = this.setContainerRef.bind(this);

        this.locale = DateTime.local().locale;
        this.onMonthSelect = this.onMonthSelect.bind(this);
        this.onYearSelect = this.onYearSelect.bind(this);
    }

    componentDidMount() {
        this.removeEventListener = addEventListener(this.container, 'transitionend', this.onTransitionEnd);
    }

    componentWillReceiveProps(nextProps) {
        const { initialMonth, numberOfMonths, orientation } = nextProps;
        const { months } = this.state;

        const { initialMonth: prevInitialMonth, numberOfMonths: prevNumberOfMonths } = this.props;
        const hasMonthChanged = !prevInitialMonth.hasSame(initialMonth, 'month');
        const hasNumberOfMonthsChanged = prevNumberOfMonths !== numberOfMonths;
        let newMonths = months;

        if (hasMonthChanged || hasNumberOfMonthsChanged) {
            if (hasMonthChanged && !hasNumberOfMonthsChanged) {
                if (isNextMonth(prevInitialMonth, initialMonth)) {
                    newMonths = months.slice(1);
                    newMonths.push(cloneDateTime(months[months.length - 1]).plus({ months: 1 }));
                } else if (isPrevMonth(prevInitialMonth, initialMonth)) {
                    newMonths = months.slice(0, months.length - 1);
                    newMonths.unshift(cloneDateTime(months[0]).minus({ months: 1 }));
                } else {
                    const withoutTransitionMonths = orientation === VERTICAL_SCROLLABLE;
                    newMonths = getMonths(initialMonth, numberOfMonths, withoutTransitionMonths);
                }
            }

            if (hasNumberOfMonthsChanged) {
                const withoutTransitionMonths = orientation === VERTICAL_SCROLLABLE;
                newMonths = getMonths(initialMonth, numberOfMonths, withoutTransitionMonths);
            }

            const loxunLocale = DateTime.local().locale;
            if (this.locale !== loxunLocale) {
                this.locale = loxunLocale;
                newMonths = newMonths.map((m) => m.setLocale(this.locale));
            }

            this.setState({
                months: newMonths,
            });
        }
    }

    componentDidUpdate() {
        const { isAnimating, transitionDuration, onMonthTransitionEnd } = this.props;

        if ((!this.isTransitionEndSupported || !transitionDuration) && isAnimating) {
            onMonthTransitionEnd();
        }
    }

    componentWillUnmount() {
        if (this.removeEventListener) this.removeEventListener();
    }

    onTransitionEnd() {
        const { onMonthTransitionEnd } = this.props;
        onMonthTransitionEnd();
    }

    onMonthSelect(currentMonth, newMonthVal) {
        const newMonth = cloneDateTime(currentMonth).set({ month: newMonthVal });
        const { onMonthChange, orientation } = this.props;
        const { months } = this.state;
        const withoutTransitionMonths = orientation === VERTICAL_SCROLLABLE;
        let initialMonthSubtraction = months.indexOf(currentMonth);
        if (!withoutTransitionMonths) {
            initialMonthSubtraction -= 1;
        }
        newMonth.minus({ months: initialMonthSubtraction });
        onMonthChange(newMonth);
    }

    onYearSelect(currentMonth, newYearVal) {
        let newMonth = cloneDateTime(currentMonth);
        const { onYearChange, orientation } = this.props;
        const { months } = this.state;
        const withoutTransitionMonths = orientation === VERTICAL_SCROLLABLE;
        let initialMonthSubtraction = months.indexOf(currentMonth);
        if (!withoutTransitionMonths) {
            initialMonthSubtraction -= 1;
        }
        newMonth = newMonth.set({ year: newYearVal }).minus({ months: initialMonthSubtraction });
        onYearChange(newMonth);
    }

    setContainerRef(ref) {
        this.container = ref;
    }

    render() {
        const {
            enableOutsideDays,
            firstVisibleMonthIndex,
            horizontalMonthPadding,
            isAnimating,
            modifiers,
            numberOfMonths,
            monthFormat,
            orientation,
            translationValue,
            daySize,
            onDayMouseEnter,
            onDayMouseLeave,
            onDayClick,
            renderMonthText,
            renderCalendarDay,
            renderDayContents,
            renderMonthElement,
            onMonthTransitionEnd,
            firstDayOfWeek,
            focusedDate,
            isFocused,
            isRTL,
            phrases,
            dayAriaLabelFormat,
            transitionDuration,
            verticalBorderSpacing,
            setMonthTitleHeight,
        } = this.props;

        const { months } = this.state;
        const isVertical = orientation === VERTICAL_ORIENTATION;
        const isVerticalScrollable = orientation === VERTICAL_SCROLLABLE;
        const isHorizontal = orientation === HORIZONTAL_ORIENTATION;

        const calendarMonthWidth = getCalendarMonthWidth(daySize, horizontalMonthPadding);

        const width =
            isVertical || isVerticalScrollable ? calendarMonthWidth : (numberOfMonths + 2) * calendarMonthWidth;

        const transformType = isVertical || isVerticalScrollable ? 'translateY' : 'translateX';
        const transformValue = `${transformType}(${translationValue}px)`;

        return (
            <div
                style={mergeStyles(
                    isAnimating &&
                        transitionDuration && {
                            transition: `transform ${transitionDuration}ms ease-in-out 0.1s`,
                        },
                    {
                        ...getTransformStyles(transformValue),
                        width,
                    }
                )}
                className={mergeClassNames(
                    'CalendarMonthGrid',
                    isHorizontal && 'CalendarMonthGrid__horizontal',
                    isVertical && 'CalendarMonthGrid__vertical',
                    isVerticalScrollable && 'CalendarMonthGrid__vertical_scrollable',
                    isAnimating && 'CalendarMonthGrid__animating'
                )}
                ref={this.setContainerRef}
                onTransitionEnd={onMonthTransitionEnd}
            >
                {months.map((month, i) => {
                    const isVisible = i >= firstVisibleMonthIndex && i < firstVisibleMonthIndex + numberOfMonths;
                    const hideForAnimation = i === 0 && !isVisible;
                    const showForAnimation = i === 0 && isAnimating && isVisible;
                    const monthString = toISOMonthString(month);
                    return (
                        <div
                            key={monthString}
                            style={mergeStyles(
                                showForAnimation &&
                                    !isVertical &&
                                    !isRTL && {
                                        position: 'absolute',
                                        left: -calendarMonthWidth,
                                    },
                                showForAnimation &&
                                    !isVertical &&
                                    isRTL && {
                                        position: 'absolute',
                                        right: 0,
                                    },
                                showForAnimation &&
                                    isVertical && {
                                        position: 'absolute',
                                        top: -translationValue,
                                    }
                            )}
                            className={mergeClassNames(
                                isHorizontal && 'CalendarMonthGrid_month__horizontal',
                                hideForAnimation && 'CalendarMonthGrid_month__hideForAnimation',
                                !isVisible && !isAnimating && 'CalendarMonthGrid_month__hidden'
                            )}
                        >
                            <CalendarMonth
                                month={month}
                                isVisible={isVisible}
                                enableOutsideDays={enableOutsideDays}
                                modifiers={modifiers[monthString]}
                                monthFormat={monthFormat}
                                orientation={orientation}
                                onDayMouseEnter={onDayMouseEnter}
                                onDayMouseLeave={onDayMouseLeave}
                                onDayClick={onDayClick}
                                onMonthSelect={this.onMonthSelect}
                                onYearSelect={this.onYearSelect}
                                renderMonthText={renderMonthText}
                                renderCalendarDay={renderCalendarDay}
                                renderDayContents={renderDayContents}
                                renderMonthElement={renderMonthElement}
                                firstDayOfWeek={firstDayOfWeek}
                                daySize={daySize}
                                focusedDate={isVisible ? focusedDate : null}
                                isFocused={isFocused}
                                phrases={phrases}
                                setMonthTitleHeight={setMonthTitleHeight}
                                dayAriaLabelFormat={dayAriaLabelFormat}
                                verticalBorderSpacing={verticalBorderSpacing}
                                horizontalMonthPadding={horizontalMonthPadding}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }
}

CalendarMonthGrid.propTypes = propTypes;
CalendarMonthGrid.defaultProps = defaultProps;

export { CalendarMonthGrid as PureCalendarMonthGrid };
export default CalendarMonthGrid;
